import * as React from 'react';
import Link from 'next/link';
import { User } from 'lucide-react';

import { GlassControl } from '@/components/atoms/GlassControl';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/primitives/avatar';
import { cn } from '@/components/atoms/primitives/utils';
import { GN_FOCUS_RING } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';

/**
 * Shared config + primitives for the marketing-city header surface.
 *
 * Both the server-default `CityHeader` (desktop bar) and the `'use client'`
 * `CityNavMenu` island (which fills the surface-neutral `NavMenuSheet` with
 * city chrome + content) consume this module so the nav link set, CTA hrefs,
 * and CTA styling are defined exactly once and can never drift
 * (WEB-481 → WEB-613). Extracted into its own file to avoid a
 * `CityHeader ⇄ CityNavMenu` import cycle.
 */

/**
 * Allowed values for `activeNav`. `null` means "no nav item is the current
 * page" (e.g. on a sub-route that doesn't map to one of the four hubs).
 */
export type CityHeaderActiveNav = 'discover' | 'neighborhoods' | 'this-week' | 'categories' | null;

interface NavItemBase {
  readonly id: Exclude<CityHeaderActiveNav, null>;
  /** Always the full product word — every breakpoint renders the same label. */
  readonly label: string;
}

/** A primary hub that navigates (`<a>`). */
export interface NavLinkItem extends NavItemBase {
  readonly kind: 'link';
  readonly href: string;
  /**
   * Set on links INTO Discover (WEB-1231 / R25): the Discover query, without
   * `?`. Render through {@link resolveNavItemHref} so a signed-in viewer gets
   * the `app.*` destination instead of the city-host `href`.
   */
  readonly discoverQuery?: string;
}

/**
 * A primary entry that opens a menu instead of navigating (WEB-1180 /
 * DES-108). Categories is the only one: its caret opens the ten canonical
 * `/category/<slug>` hubs (`CategoriesDisclosure`). It has no `href` on
 * purpose — `/categories` does not exist.
 */
export interface NavDisclosureItem extends NavItemBase {
  readonly kind: 'disclosure';
  readonly id: 'categories';
}

export type NavItem = NavLinkItem | NavDisclosureItem;

/**
 * Single source of truth for the primary nav entry set. Consumed by both the
 * desktop nav row and `CityMenuSheetContent` so the two never drift
 * (WEB-481 → WEB-614). The sitemap renders only the {@link NAV_HUB_LINKS};
 * the Categories disclosure becomes its chip group there (DES-108).
 */
export const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { kind: 'link', id: 'discover', href: '/discover', discoverQuery: '', label: 'Discover' },
  // FRE-1362 reverts WEB-618 / PL-06's "Hoods" tablet abbreviation — the
  // design instead trims the brand + tools cluster at the 721–1024 band, so
  // every breakpoint keeps the full "Neighborhoods" word.
  { kind: 'link', id: 'neighborhoods', href: '/neighborhoods', label: 'Neighborhoods' },
  // WEB-1239: "This week" is the API `this-week` keyword (now through Sunday,
  // city-local), not the rolling `next7` window — label and link name one window.
  {
    kind: 'link',
    id: 'this-week',
    href: '/discover?date=this-week',
    discoverQuery: 'date=this-week',
    label: 'This week',
  },
  { kind: 'disclosure', id: 'categories', label: 'Categories' },
];

/** The primary entries that are real routes — the sitemap's hub rows. */
export const NAV_HUB_LINKS: ReadonlyArray<NavLinkItem> = NAV_ITEMS.filter(
  (item): item is NavLinkItem => item.kind === 'link',
);

/**
 * The href a nav item renders: Discover-bound items go through the
 * `useDiscoverHref` builder (signed in → `app.*`, WEB-1231 / R25); every
 * other hub is a city-host page and keeps its relative `href`.
 */
export function resolveNavItemHref(
  item: NavLinkItem,
  toDiscover: (query?: string) => string,
): string {
  return item.discoverQuery === undefined ? item.href : toDiscover(item.discoverQuery);
}

/**
 * Secondary destinations currently unreachable from the city header band
 * alone (WEB-614 / F-02). Consumed by `CityMenuSheetContent`. `search` opens
 * the shared `useSearchSheet` store — no second search entry point.
 *
 * WEB-639: Venues + Artists are omitted until those pages launch — secondary
 * nav ships Search Charlotte only (README "Deliberate omissions").
 */
export type CitySecondaryNavId = 'search';

export interface CitySecondaryNavItem {
  readonly id: CitySecondaryNavId;
  /**
   * Static label. For `search`, the menu interpolates via
   * `formatSearchCityLabel(cityName)` (WEB-620 / PL-09) — this field is the
   * stem used in stories/docs only.
   */
  readonly label: string;
}

export const SECONDARY_NAV_ITEMS: ReadonlyArray<CitySecondaryNavItem> = [
  { id: 'search', label: 'Search' },
];

/**
 * Re-export of the shared global-nav focus ring. The constant itself lives in
 * `GlobalNavCoordStrip` so surface-neutral primitives (`NavMenuSheet`) can use
 * it without importing this city-scoped module.
 */
export const FOCUS_RING = GN_FOCUS_RING;

// Actions collapse visually below `sm` (640px) without removing them from the
// accessibility tree (the mobile drawer surfaces the same CTAs). The collapse is
// scoped to `max-sm`: `max-sm:sr-only` hides the pill below `sm` while keeping it
// keyboard-reachable, `max-sm:focus:not-sr-only` reveals it on focus, and from
// `sm` up the base `inline-flex` pill simply shows (see docs/verification/city-home-mobile-qa/README.md).
//
// Why `max-sm` scoping rather than the old `sr-only … sm:not-sr-only`: the DS
// pill (`o-globalnav__cta`, 36px tall, `0 16px` padding) is built from base
// utilities (`h-9 px-4`), but `not-sr-only` *resets* `padding`/`height`/`width`.
// As a `sm:`-variant, `sm:not-sr-only` sorted AFTER base `px-4`/`h-9` in the
// cascade and won at ≥640px — zeroing horizontal padding and height. `min-w-11`/
// `min-h-11` survived (different properties), so the short "Sign up" text
// collapsed to a 47×44 amber circle (FRE-870 parity bug). Scoping `not-sr-only`
// to `max-sm` means it is absent from the cascade at `sm+`, so `px-4`/`h-9` apply
// unopposed — no reliance on Tailwind's within-variant sort order.
//
// `min-w-11 min-h-11` (44px = 11 × 4) keeps the WCAG 2.5.5 tap-target floor on
// both axes (WEB-306 audit measured the CTAs at 24×44 / 32×44 — width was the
// gap). `lg:min-h-9` steps the effective height back to the DS's 36px on desktop,
// mirroring the search pill's `lg:min-h-9` reconciliation; the px-4 text width
// already clears the 44px floor, so the pill renders at the DS spec.
const ACTION_BASE_CORE =
  'inline-flex min-h-11 min-w-11 h-9 lg:min-h-9 items-center justify-center rounded-full px-4 font-display text-small font-semibold leading-tight transition-colors duration-150 motion-reduce:transition-none';

// `max-sm:sr-only` hides a pill below `sm` (640px) while keeping it
// keyboard-reachable. Used by the ghost "Open app" secondary when mounted.
const SM_HIDDEN = 'max-sm:sr-only max-sm:focus:not-sr-only';

// Ghost CTA (DS `o-globalnav__cta--ghost`): transparent fill, glass border that
// brightens to white on hover. Shared by the demoted "Open app" secondary and
// retained `SIGN_IN_LINK` export for surfaces that still need a ghost pill.
const GHOST_CTA_CORE = `${ACTION_BASE_CORE} border border-white/[0.16] bg-transparent text-fg-1 hover:border-white`;
const GHOST_CTA = `${GHOST_CTA_CORE} ${SM_HIDDEN}`;

/**
 * Retained ghost "Sign in" pill style for non-header surfaces. The city desktop
 * bar no longer mounts this CTA — WEB-617 / DES-59 replaces both auth pills
 * with the account chooser (`CityAccountChooser`).
 */
export const SIGN_IN_LINK = GHOST_CTA;

/**
 * Accessible name for the city-header anon account control (WEB-617 / DES-59).
 * Verbatim — names both paths; since FRE-1389 the control opens a compact
 * chooser that carries both (`CityAccountChooser`).
 */
export const ACCOUNT_AFFORDANCE_ARIA_LABEL = 'Account, sign in or sign up' as const;

/**
 * Pending-slot name (WEB-640 / WEB-793). Neutral — no conversion copy — so a
 * signed-in visitor never hears "sign up" while `/auth/me` is in flight. The
 * control is still a real link so a hung probe cannot trap conversion.
 */
export const PENDING_ACCOUNT_ARIA_LABEL = 'Account' as const;

/** Accessible name for the authed city-header avatar affordance (WEB-640). */
export function authedAccountAriaLabel(displayName: string): string {
  const trimmed = displayName.trim();
  return trimmed.length > 0 ? `Account, ${trimmed}` : 'Account';
}

/**
 * Call-site skin for the account control, rendered by the `GlassControl` atom
 * at the nav size (WEB-1021): a 36px glass circle (DS 1.9.0
 * `.o-globalnav__icon-btn`) whose coarse-pointer 44 `::before` hit floor comes
 * from the atom. Visible at every width — no `sr-only` / `hidden`. `--fg-2`
 * keeps the glyph a notch quieter than the accent wash it sits in on hover.
 */
export const ACCOUNT_AFFORDANCE_CONTROL = 'shrink-0 text-fg-2';

/**
 * Append `from=city-nav` so the conversion tradeoff is measurable from day one
 * (WEB-617). Absolute URLs keep their origin; relative URLs are parsed against a
 * dummy base so existing `from` values are replaced and fragments are preserved.
 */
export function cityNavSignUpHref(signUpUrl: string): string {
  try {
    const absolute = /^https?:\/\//i.test(signUpUrl);
    const url = absolute ? new URL(signUpUrl) : new URL(signUpUrl, 'https://loci.invalid');
    url.searchParams.set('from', 'city-nav');
    if (absolute) {
      return url.toString();
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return signUpUrl;
  }
}

export interface CityAuthAvatarProps {
  readonly authUser: CityAuthUser;
  /** Size + initials type size, e.g. `size-7 text-micro`. */
  readonly className?: string;
  readonly testId?: string;
}

/**
 * The signed-in visitor's avatar (WEB-640 / FRE-1389): their photo when
 * `/auth/me` has one, otherwise real initials on the DS ember → skyline
 * gradient (`.o-globalnav__avatar-img`). Decorative — the control it sits in
 * carries the accessible name. Shared by the header trigger and the account
 * drawer's identity head so the two can never drift.
 */
export function CityAuthAvatar({
  authUser,
  className,
  testId,
}: CityAuthAvatarProps): React.ReactElement {
  return (
    <Avatar
      aria-hidden="true"
      data-testid={testId}
      className={cn('border-0 p-0 font-display font-semibold', className)}
    >
      {authUser.avatarUrl ? <AvatarImage src={authUser.avatarUrl} alt="" /> : null}
      <AvatarFallback className="bg-[linear-gradient(135deg,var(--clt-ember),var(--clt-skyline))] text-white">
        {authUser.initials}
      </AvatarFallback>
    </Avatar>
  );
}

export interface PendingAccountAffordanceProps {
  /** Signup destination; `from=city-nav` is appended (same as the chooser's Sign up). */
  readonly signUpUrl: string;
  readonly className?: string;
}

/**
 * Neutral account slot while cookie/hint-armed `/auth/me` is in flight
 * (WEB-640). Same geometry as anon/authed so the tools cluster does not
 * reflow — and deliberately neither the anon "sign in or sign up" label nor
 * any authed identity, so signed-in visitors never flash the conversion CTA.
 *
 * WEB-793: remains a real `<Link>` (not `pointer-events-none`). A hung or
 * 5xx probe used to leave the only visible account control untargetable.
 */
export function PendingAccountAffordance({
  signUpUrl,
  className,
}: PendingAccountAffordanceProps): React.ReactElement {
  const href = cityNavSignUpHref(signUpUrl);
  const external = isCrossOrigin(href);
  return (
    <GlassControl
      size="nav"
      asChild
      className={cn(ACCOUNT_AFFORDANCE_CONTROL, 'opacity-60', className)}
    >
      <Link
        href={href}
        aria-busy="true"
        aria-label={PENDING_ACCOUNT_ARIA_LABEL}
        data-testid="city-header-account"
        data-auth="pending"
        {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        <User aria-hidden="true" className="size-4 shrink-0" strokeWidth={2} />
      </Link>
    </GlassControl>
  );
}

/**
 * "Open app" — demoted to the ghost secondary so the amber "Sign up" reads as
 * the single filled primary (DS shows one filled CTA in the tools cluster;
 * stacking two filled pills muddies the hierarchy — FRE-873).
 */
export const OPEN_APP_LINK = GHOST_CTA;

/**
 * "Sign up" — the amber primary CTA (DS `o-globalnav__cta`, FRE-873). Painted
 * through `--loci-amber` (the national Electric Amber accent) rather than the
 * city layer's `--gn-accent` (skyline teal), so the conversion CTA is visually
 * distinct from the in-surface accent. `--loci-indigo` foreground matches the
 * existing amber-CTA precedent (CityFooter download pill) and keeps AA contrast
 * on the bright amber fill in both themes.
 *
 * FRE-893: the indigo fg needs BOTH the explicit `color:` data-type hint AND a
 * trailing `!` (the FRE-888 CTA precedent). (1) Tailwind v4 can't infer
 * color-vs-length from a bare `text-[var(--loci-indigo)]`, so the utility never
 * compiles. (2) globals.css ships an UNLAYERED `a { color: inherit }` reset that
 * beats every `@layer utilities` declaration, so on this `<a>` the indigo
 * utility lost and the pill inherited the nav's text colour — white in dark
 * theme → white-on-amber #FFB800 = 1.73:1 (the axe failure). Indigo #1E2B4B on
 * #FFB800 ≈ 11:1 in both themes (`--loci-amber` is theme-invariant).
 */
export const SIGN_UP_LINK = `${ACTION_BASE_CORE} bg-loci-amber text-loci-indigo! hover:brightness-110`;

/** Accessible name for the chooser popover (DS 1.9.1 `buildAuth`). */
export const ACCOUNT_CHOOSER_ARIA_LABEL = 'Sign in or sign up' as const;

// FRE-1389 — DS 1.9.1 `.o-globalnav__cta--block`: full width, 48px floor, 15px,
// and allowed to wrap (centered) as a safety on the narrowest chooser. Merged
// with `cn` so it replaces the header pill's `h-9` / `lg:min-h-9` / `text-small`.
const CTA_BLOCK =
  'h-auto min-h-12 lg:min-h-12 w-full py-3 text-body text-center whitespace-normal text-balance';

/** The chooser's amber "Sign up — build your footprint" CTA (FRE-1389). */
export const CHOOSER_SIGN_UP_LINK = cn(SIGN_UP_LINK, CTA_BLOCK);

/**
 * The chooser's ghost "Sign in" CTA (FRE-1389, DS 1.9.1
 * `.o-globalnav__cta--ghost`). Never {@link SIGN_IN_LINK}: its `max-sm:sr-only`
 * is for the bar and would hide Sign in on phones (the collision DS 1.9.1
 * fixed). The edge is the theme-aware `--glass-border-strong` (hover `--fg-1`)
 * rather than the bar's white hairline, which vanishes on the light chooser.
 */
export const CHOOSER_SIGN_IN_LINK = cn(
  ACTION_BASE_CORE,
  'border border-ds-glass-border-strong bg-transparent text-fg-1 hover:border-fg-1 hover:bg-ds-glass-bg',
  CTA_BLOCK,
);

export function isCrossOrigin(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export interface ActionLinkProps {
  href: string;
  label: string;
  className: string;
  testId: string;
}

/**
 * Renders a Sign in / Open app CTA. Cross-origin hrefs open in a new tab with
 * `rel="noreferrer"`. Shared so the mobile drawer reuses the exact same
 * href/target/rel resolution as the desktop actions (WEB-481).
 */
export function ActionLink({
  href,
  label,
  className,
  testId,
}: ActionLinkProps): React.ReactElement {
  const external = isCrossOrigin(href);
  return (
    <Link
      href={href}
      className={cn(className, FOCUS_RING)}
      data-testid={testId}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      {label}
    </Link>
  );
}
